import { Divider } from "@blueprintjs/core";
import {
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  useApiEffect,
} from "nsitools-react";
import * as React from "react";

import { CountryApi, PersonDto } from "../../../../../api";
import {
  FGCountrySelect,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
} from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IPersonSplitFormProps {
  person: PersonDto;
  personId: number;
  fgName: string;
  readonly?: boolean;
}

export const PersonSplitForm: React.FunctionComponent<IPersonSplitFormProps> = ({
  person,
  personId,
  fgName,
  readonly = false,
}) => {
  const countryApi = useApiService(CountryApi);

  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const [postalCode, setPostalCode] = React.useState("");
  const [isBelgium, setIsBelgium] = React.useState<boolean>(false);
  const [baseCountryId] = useApiEffect(
    async () => await countryApi.getBelgianId(),
    []
  );

  const [languages, lloading] = useReferential(
    (a) => a.getLanguages(),
    true,
    []
  );
  const [postalCodes, pcloading] = useReferential(
    (a) => a.getStringifiedBelgianPostalCodes(),
    true,
    []
  );
  const [localities, locloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(postalCode),
    false,
    [postalCode]
  );
  const [personSalutations, ploading] = useReferential(
    (a) => a.getPersonSalutations(currentLanguage.languageId),
    true,
    [currentLanguage.languageId]
  );

  return (
    <FieldGroup>
      <FGListen
        field="countryId"
        onChanged={(value, formik) => {
          if (!value) {
            formik.setFieldValue("countryId", baseCountryId);
            setIsBelgium(true);
          } else {
            if (value === baseCountryId) {
              setIsBelgium(true);
            } else {
              setIsBelgium(false);
            }
          }
        }}
      />
      <FGListen
        field="postalCode"
        onChanged={(value) => {
          if (isBelgium) {
            setPostalCode(value);
          }
        }}
      />
      <FGSelectInput
        name={`${fgName}.personHonorificId`}
        items={personSalutations}
        loading={ploading}
        label={t(ETLCodes.Salutation)}
        requiredMark
      />
      <FGTextInput
        name={`${fgName}.lastName`}
        label={t(ETLCodes.LastName)}
        requiredMark
        readonly={readonly}
      />
      <FGTextInput
        name={`${fgName}.firstName`}
        label={t(ETLCodes.FirstName)}
        requiredMark
        readonly={readonly}
      />
      <FGTextInput
        name={`${fgName}.email`}
        label={t(ETLCodes.Email)}
        readonly={readonly}
        requiredMark
      />
      <FGTextInput
        name={`${fgName}.phoneNumber`}
        label={t(ETLCodes.PhoneNumber)}
        requiredMark
        readonly={readonly}
      />
      <FGTextInput
        name={`${fgName}.mobilePhoneNumber`}
        label={t(ETLCodes.MobilePhoneNumber)}
        requiredMark
        readonly={readonly}
      />
      <FGSelectInput
        name={`${fgName}.languageId`}
        items={languages}
        loading={lloading}
        label={t(ETLCodes.Language)}
        requiredMark
        readonly={readonly}
      />
      <Divider />
      <FGSRSHDateMaskInput
        name={`${fgName}.deathDate`}
        label={t(ETLCodes.DeathDate)}
        readonly={readonly}
      />
      <FGSRSHCheckboxInput
        name={`${fgName}.isBadPayer`}
        label={t(ETLCodes.IsBadPayer)}
        readonly={readonly}
      />
      <FGSRSHDateMaskInput
        name={`${fgName}.bannedFrom`}
        label={t(ETLCodes.BannedFrom)}
        readonly={readonly}
      />{" "}
      <FGSRSHDateMaskInput
        name={`${fgName}.bannedTo`}
        label={t(ETLCodes.BannedTo)}
        readonly={readonly}
      />
      <Divider />
      <FGTextInput
        name={`${fgName}.street`}
        label={t(ETLCodes.Street)}
        readonly={readonly}
      />
      <FGTextInput
        name={`${fgName}.number`}
        label={t(ETLCodes.StreetNumber)}
        readonly={readonly}
      />
      {isBelgium ? (
        <FGSelectInput
          name={`${fgName}.postalCode`}
          items={postalCodes}
          loading={pcloading}
          label={t(ETLCodes.PostalCode)}
          readonly={readonly}
        />
      ) : (
        <FGTextInput
          name={`${fgName}.postalCode`}
          label={t(ETLCodes.PostalCode)}
          readonly={readonly}
        />
      )}
      {isBelgium ? (
        <FGSelectInput
          name={`${fgName}.city`}
          items={localities}
          loading={locloading}
          label={t(ETLCodes.City)}
          readonly={readonly}
        />
      ) : (
        <FGTextInput
          name={`${fgName}.city`}
          label={t(ETLCodes.City)}
          readonly={readonly}
        />
      )}
      <FGCountrySelect
        name={`${fgName}.countryId`}
        label={t(ETLCodes.Country)}
        readonly={readonly}
      />
      <FGTextAreaInput
        name={`${fgName}.remark`}
        label={t(ETLCodes.Remark)}
        readonly={readonly}
      />
      <Divider />
      <FGTextInput
        name={`${fgName}.vatNumber`}
        label={t(ETLCodes.VATNumber)}
        maxLength={20}
        readonly={readonly}
      />
      <FGTextInput
        name={`${fgName}.societyName`}
        label={t(ETLCodes.SocietyName)}
        maxLength={50}
        readonly={readonly}
      />
    </FieldGroup>
  );
};
